import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, 
  Shield, 
  Zap, 
  Smartphone, 
  Database, 
  Cloud,
  ArrowRight,
  CheckCircle,
  Star
} from 'lucide-react';

const HomePage: React.FC = () => {
  const features = [
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Real-time insights and comprehensive data visualization for your business metrics.',
      color: 'bg-primary-100 text-primary-700'
    },
    {
      icon: Shield,
      title: 'Secure Authentication',
      description: 'Enterprise-grade security with JWT tokens and role-based access control.',
      color: 'bg-neutral-100 text-neutral-700'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized performance with React 18, Vite, and modern web technologies.',
      color: 'bg-primary-100 text-primary-700'
    },
    {
      icon: Smartphone,
      title: 'Mobile Ready',
      description: 'Cross-platform mobile support with Capacitor for iOS and Android.',
      color: 'bg-neutral-100 text-neutral-700'
    },
    {
      icon: Database,
      title: 'Scalable Backend',
      description: 'Node.js API with PostgreSQL database and Redis caching.',
      color: 'bg-primary-100 text-primary-700'
    },
    {
      icon: Cloud,
      title: 'Cloud Storage',
      description: 'S3-compatible file storage with MinIO for scalable object storage.',
      color: 'bg-neutral-100 text-neutral-700'
    }
  ];

  const stats = [
    { label: 'Active Users', value: '10K+' },
    { label: 'Data Points', value: '1M+' },
    { label: 'Uptime', value: '99.9%' },
    { label: 'Response Time', value: '<100ms' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center px-4 py-2 rounded-2xl bg-primary-100 text-primary-700 text-sm font-medium">
                <Star className="w-4 h-4 mr-2" />
                Now with Enhanced Design System
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-primary-700 mb-6 font-gilroy">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                CapitolCommons
              </span>
        </h1>
            
            <p className="text-xl md:text-2xl text-neutral-600 mb-8 max-w-3xl mx-auto font-poppins">
              A modern, scalable full-stack application with React frontend, Node.js backend, 
              and mobile support using Capacitor.
        </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
              <Link to="/dashboard" className="inline-block font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg bg-primary-700 text-white hover:bg-primary-800 focus:ring-primary-500 dark:bg-primary-600 dark:hover:bg-primary-700 text-lg px-8 py-3">
            Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/register" className="inline-block font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary focus:ring-primary-500 text-lg px-8 py-3">
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-700 mb-2 font-gilroy">
                  {stat.value}
                </div>
                <div className="text-neutral-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-primary-700 mb-4 font-gilroy">
              Powerful Features
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Everything you need to build and scale your modern web application
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="card hover:shadow-lg transition-shadow duration-300">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4 ${feature.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-primary-700 mb-3 font-gilroy">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6 font-gilroy">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of developers building the future with CapitolCommons
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/register" className="inline-block font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg bg-white text-primary-700 hover:bg-primary-50 text-lg px-8 py-3">
              Start Building
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link to="/login" className="inline-block font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg border-2 border-white text-white hover:bg-white hover:text-primary-700 text-lg px-8 py-3">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
            <span className="text-white font-medium">Free to get started</span>
          </div>
          <p className="text-neutral-300 mb-6">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
          <Link to="/register" className="inline-block font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg bg-primary-700 text-white hover:bg-primary-800 focus:ring-primary-500 dark:bg-primary-600 dark:hover:bg-primary-700 text-lg px-8 py-3">
            Get Started for Free
          </Link>
      </div>
      </section>
    </div>
  );
};

export default HomePage; 